import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../utils/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import BackButton from '../../components/BackButton';

const AddShift = () => {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    clientId: '',
    date: '',
    startTime: '',
    endTime: '',
    recurrence: 'none',
    title: '',
    allowOverlap: false
  });
  const [conflict, setConflict] = useState(null);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const prefillDate = searchParams.get('date');
    if (prefillDate) {
      setFormData((prev) => ({ ...prev, date: prefillDate }));
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClients(res.data);
      } catch (err) {
        toast.error('Failed to load clients');
      }
    };
    if (token) fetchClients();
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setFormData(prev => ({ ...prev, date: formattedDate }));
  };

  const handleTimeChange = (name, dateOrRawInput) => {
    if (!dateOrRawInput) return;

    let formattedTime;

    // Case 1: User types a raw string like "1000", "930", etc.
    if (typeof dateOrRawInput === 'string') {
      let raw = dateOrRawInput.replace(':', '').trim();

      if (/^\d{3,4}$/.test(raw)) {
        if (raw.length === 3) raw = '0' + raw; // 930 -> 0930
        const hh = raw.substring(0, 2);
        const mm = raw.substring(2, 4);
        formattedTime = `${hh}:${mm}`;
      } else if (/^\d{1,2}:\d{2}$/.test(dateOrRawInput)) {
        formattedTime = dateOrRawInput;
      } else {
        // toast.error('Invalid time format. Use HHmm or HH:mm');
        return;
      }
    }

    // Case 2: Date object from DatePicker
    if (typeof dateOrRawInput === 'object') {
      formattedTime = dayjs(dateOrRawInput).format('HH:mm');
    }

    setFormData(prev => ({ ...prev, [name]: formattedTime }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE_URL}/api/shifts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(res)
      toast.success('Shift booked successfully!');
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.conflicts) {
        setConflict(err.response.data.conflicts);
      } else {
        toast.error('Failed to book shift');
      }
    }
  };

  const closeConflictPopup = () => setConflict(null);

  return (
    <div className="min-h-screen bg-[#0E0A1F] text-white p-6 relative">
      <BackButton />
      <div className="max-w-xl mx-auto bg-[#1C1235] p-6 rounded-xl space-y-4">
        <h2 className="text-xl font-bold">Add Shift</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 text-sm">Shift Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Doctor appointment"
              className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
            />
          </div>

          <div>
            <label className="block mb-1 text-sm">Client</label>
            <select
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              required
              className="w-full p-3 rounded bg-[#2A1C4A] text-white"
            >
              <option value="">Select client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-sm">Date</label>
            <DatePicker
              selected={formData.date ? new Date(formData.date) : null}
              onChange={handleDateChange}
              dateFormat="dd/MM/yy"
              placeholderText="Select date"
              className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block mb-1 text-sm">Start Time (24h)</label>
              <DatePicker
                selected={formData.startTime ? dayjs(`2000-01-01T${formData.startTime}`).toDate() : null}
                onChange={(date) => handleTimeChange('startTime', date)}
                onChangeRaw={(e) => handleTimeChange('startTime', e.target.value)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={5}
                timeCaption="Start Time"
                dateFormat="HH:mm"
                placeholderText="Select time"
                className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
              />
            </div>
            <div className="flex-1">
              <label className="block mb-1 text-sm">End Time (24h)</label>
              <DatePicker
                selected={formData.endTime ? dayjs(`2000-01-01T${formData.endTime}`).toDate() : null}
                onChange={(date) => handleTimeChange('endTime', date)}
                onChangeRaw={(e) => handleTimeChange('endTime', e.target.value)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={5}
                timeCaption="End Time"
                dateFormat="HH:mm"
                placeholderText="Select time"
                className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="allowOverlap"
              checked={formData.allowOverlap}
              onChange={handleChange}
              className="w-4 h-4"
            />
            <label className="text-sm">Allow overlapping shifts</label>
          </div>

          <div>
            <label className="block mb-1 text-sm">Repeat</label>
            <select
              name="recurrence"
              value={formData.recurrence}
              onChange={handleChange}
              className="w-full p-3 rounded bg-[#2A1C4A] text-white"
            >
              <option value="none">None</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {formData.recurrence !== 'none' && (
  <div>
    <label className="block text-sm mb-1">Repeat Until</label>
    <DatePicker
      selected={formData.repeatUntil ? dayjs(formData.repeatUntil).toDate() : null}
      onChange={(date) =>
        setFormData((prev) => ({
          ...prev,
          repeatUntil: date ? dayjs(date).format('YYYY-MM-DD') : ''
        }))
      }
      dateFormat="yyyy-MM-dd"
      placeholderText="Select end date"
      className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
      minDate={formData.date ? new Date(formData.date) : new Date()} // prevent selecting before start
    />
  </div>
)}


          <button
            type="submit"
            className="w-full py-3 bg-[#8F4EFF] hover:bg-purple-700 rounded font-semibold"
          >
            Save Shift
          </button>
        </form>
      </div>

      {conflict && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
          <div className="bg-white text-black p-6 rounded-xl max-w-lg w-full overflow-y-auto max-h-[80vh]">
            <h3 className="text-xl font-bold mb-4 text-center">Conflicting Shifts Detected</h3>

            {conflict.map((conflicts) => (
              <div key={conflicts.id} className="border-b border-gray-200 pb-3 mb-3 text-sm">
                {conflicts.title && (
                  <p className="mb-1"><strong>Title:</strong> {conflicts.title}</p>
                )}
                <p className="mb-1"><strong>Client:</strong> {conflicts.Client?.name}</p>
                <p className="mb-1"><strong>Time:</strong> {conflicts.startTime} - {conflicts.endTime}</p>
                {conflicts.recurrence !== 'none' && (
                  <p className="mb-1"><strong>Repeats:</strong> {conflicts.recurrence}</p>
                )}
                {/* <Link
                  to={`/shifts/${conflicts.id}/edit`}
                  className="inline-block mt-2 bg-blue-600 text-white text-xs px-4 py-1.5 rounded hover:bg-blue-700"
                >
                  View Shift
                </Link> */}
                <Link
                    to={`/shifts/${conflicts.id}/edit`}
                    state={{ returnTo: '/shifts/new' }}
                    className="inline-block mt-2 bg-blue-600 text-white text-xs px-4 py-1.5 rounded hover:bg-blue-700"
                  >
                    View Shift
                  </Link>

              </div>
            ))}

            <button
              onClick={closeConflictPopup}
              className="w-full mt-2 bg-gray-600 text-white text-sm py-2 rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddShift;
